import React, { useEffect, useState } from "react";
import PropTypes from "kf-proptypes/widgets";
import { Trans, t } from "@lingui/macro";

import { getS3FileKey, isEmpty } from "base/utils";
import { useAccountStore, useUserStore } from "base/stores";
import { THEME, SIGNATURE_PEN_COLOR } from "base/constants";

import { CircleProgress, Tooltip } from "@widgets/feedback/index.jsx";
import { Button, BUTTON_TYPES } from "../button/index.jsx";
import { Modal } from "../../datadisplay/modal/index.jsx";
import { Signature as SignatureWidget } from "./signaturewidget.js";
import { LazyLoadComponent } from "@widgets/others/lazyloader/index.jsx";
import {
  ICON_PADDING,
  ICON_SIZE,
  ICON_STROKE,
  KF_ICONS,
  KfIcon
} from "@widgets/common/components/primitives/kf_icon/index.jsx";

import styles from "./signature.css";

const SIGNATURE_FILE_PICKER_CONFIG = {
  fileExtensions: ["JPG", "JPEG", "JPE", "PNG"],
  imageProps: {
    crop: true,
    aspectRatio: 7 / 3,
    imageViewMode: true,
    canvasColor: "white"
  },
  maxSize: 10485760,
  maxCount: 1,
  isSignature: true,
  isCropMandatory: true,
  validSelectedFile: []
};

export function SignaturePopup({
  readOnly,
  uploadSignature,
  enableAttachment,
  useMySignature,
  clearSignature,
  clearValue,
  value,
  closePopup,
  onSave,
  disableUseSignature,
  showLoading,
  handleAttachment,
  uploadType,
  uploadedData,
  strDataURI,
  imageData,
  isPublicForm,
  prefix,
  onFileAttached
}) {
  const theme =
    useUserStore((state) => state.theme) || localStorage.getItem("Theme");
  let penColor =
    theme === THEME.LIGHT
      ? SIGNATURE_PEN_COLOR.LIGHT
      : SIGNATURE_PEN_COLOR.DEFAULT;

  const [signatureFilePicker, setSignatureFilePicker] = useState();
  const [imageFileObj, setImageFileObj] = useState({});

  const [progress, setProgress] = useState(0);
  const accountDetails = useAccountStore.getState();

  useEffect(
    function fileEffect() {
      if (imageFileObj.onProgress && imageFileObj.onResponseDone) {
        imageFileObj.onProgress((data) => {
          setProgress(data.uploaded || 0);
        });
        imageFileObj.onResponseDone((err, data) => {
          onFileAttached(data);
          setImageFileObj({});
        });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [Object.keys(imageFileObj).length]
  );

  const mySignature = () => {
    if (isPublicForm) {
      return null;
    } else {
      return (
        <div className={styles.leftActionsDiv}>
          {enableAttachment ? (
            <LazyLoadComponent
              component={() =>
                import(/* webpackPrefetch: true */ "kfplatform/filePicker")
              }
              componentName="FilePickerV2"
              open={signatureFilePicker}
              onFileChoosen={(value) => {
                setImageFileObj(value[0]);
              }}
              prefix={prefix}
              onPickerClose={() => {
                setSignatureFilePicker(false);
              }}
              options={SIGNATURE_FILE_PICKER_CONFIG}
              baseURL={`/upload/2/${accountDetails._id}/`}
            >
              <Button
                type={BUTTON_TYPES.SECONDARY}
                onClick={(e) => {
                  setSignatureFilePicker(true);
                  handleAttachment(e);
                }}
              >
                <Trans>Upload</Trans>
              </Button>
            </LazyLoadComponent>
          ) : (
            <Button
              type={BUTTON_TYPES.SECONDARY}
              onClick={useMySignature}
              className={
                disableUseSignature ? styles.disableUseSignature : null
              }
              disabled={disableUseSignature}
            >
              <Trans>Use my signature</Trans>
            </Button>
          )}
          {disableUseSignature && !enableAttachment && (
            <Tooltip
              position="top"
              content={t`No signature has been added yet`}
            >
              <KfIcon
                icon={KF_ICONS.INFO_WITH_CIRCLE}
                size={ICON_SIZE.SIZE_16}
                stroke={ICON_STROKE.THIN}
                padding={ICON_PADDING.PADDING_8}
              />
            </Tooltip>
          )}
        </div>
      );
    }
  };

  const signatureFooter = () => {
    return (
      <div className={styles.footer}>
        <div
          onClick={(e) => {
            setImageFileObj({});
            clearSignature(e);
          }}
          className={`${styles.clearSignature}`}
        >
          <Trans>Clear</Trans>
        </div>
        <Button
          type={BUTTON_TYPES.PRIMARY}
          onClick={onSave}
          isLoading={showLoading}
        >
          <Trans>Save</Trans>
        </Button>
      </div>
    );
  };

  const imageFile = imageFileObj?.file || "";

  return (
    <Modal
      titleRenderer={() => t`Draw your signature`}
      open={true}
      onClose={() => closePopup()}
      rightActionsRenderer={signatureFooter}
      className={styles.signatureModal}
      leftActionsRenderer={mySignature}
    >
      {uploadType === "attach" && (uploadedData || imageFile) ? (
        <>
          {imageFile && (
            <div className={styles.uploadingThumnailContainer}>
              {imageFile?.thumbnailImg && (
                <img
                  className={styles.attachedImg}
                  src={imageFile.thumbnailImg}
                  alt={imageFile.name}
                />
              )}

              {!imageFile?.fileError && progress !== 100 && (
                <div className={styles.overlay}>
                  <CircleProgress
                    type={"circle"}
                    percent={progress}
                    width={32}
                    strokeColor={"var(--kf-color-primary-500)"}
                    trailColor="var(--kf-color-gray-200)"
                    showInfo={true}
                    strokeWidth={12}
                    status={"normal"}
                    format={(percent) => <span>{percent}%</span>}
                  />
                </div>
              )}
            </div>
          )}
          {isEmpty(imageFile) && uploadedData && (
            <div
              className={styles.attachedImg}
              alt=""
              style={{
                backgroundImage: `url('/upload/2/${getS3FileKey(
                  uploadedData["key"]
                )}')`,
                backgroundPosition: "center",
                backgroundRepeat: "no-repeat",
                backgroundSize: "contain"
              }}
            />
          )}
        </>
      ) : (
        <SignatureWidget
          edit={!readOnly}
          readOnly={readOnly}
          handleOnEnd={(value, imageData) => uploadSignature(value, imageData)}
          className={styles.signature}
          value={value && value["key"]}
          width="520"
          height="190"
          clear={clearValue}
          strDataURI={strDataURI}
          imageData={imageData}
          isPublicForm={isPublicForm}
          penColor={penColor}
        />
      )}
    </Modal>
  );
}

SignaturePopup.propTypes = {
  uploadSignature: PropTypes.func,
  readOnly: PropTypes.bool,
  enableAttachment: PropTypes.bool,
  useMySignature: PropTypes.func,
  clearSignature: PropTypes.func,
  clearValue: PropTypes.bool,
  value: PropTypes.shape({
    name: PropTypes.string,
    key: PropTypes.string
  }),
  closePopup: PropTypes.func,
  onSave: PropTypes.func,
  disableUseSignature: PropTypes.bool,
  showLoading: PropTypes.bool,
  handleAttachment: PropTypes.func,
  uploadType: PropTypes.string,
  uploadedData: PropTypes.shape({
    name: PropTypes.string,
    key: PropTypes.string
  }),
  strDataURI: PropTypes.string,
  imageData: PropTypes.string,
  isPublicForm: PropTypes.bool,
  prefix: PropTypes.string,
  onFileAttached: PropTypes.func
};
